import {useParams, useSearchParams} from 'react-router-dom';
import RichList, {RichListItem} from '../components/RichList';
import {CardContent, Grid, LinearProgress, Typography} from '@mui/material';
import {useAddress} from '../queries';
import QueryProcessor from '../components/QueryProcessor';
import {TabbedMainCard} from '../components/MainCard';
import {showAmount, SmartLink} from '../abelcommon';
import React from "react";
import ErrorCard from "../components/ErrorCard";
import useAddressTxs from "../queries/useAddressTxs";
import PaginationCard from "../components/PaginationCard";
import TxList from "../components/TxList";
import {messageForPrivacyLevel} from '../components/PrivacyLevel';

function useAddressWithPrecheck(address?: string) {
    if (!address || !address.startsWith('0xabe')) {
        throw new Error('Illegal address.');
    }
    return useAddress(address);
}

function AddressOverview({d}: { d: any }) {
    const {address} = useParams();

    return (
        <RichList sx={{width: 1, mt: -2}}>
            <RichListItem
                title={'Address (Short)'}
                help={'The 66-byte or 68-byte short address in which the last 32 bytes are unknown to the public.'}
            >
                <Typography variant={'body2'}>
                    {address}
                    {address && '*'.repeat(64)}
                </Typography>
            </RichListItem>
            <RichListItem
                title={'Privacy Level'}
                help={'The 66-byte or 68-byte short address in which the last 32 bytes are unknown to the public.'}
            >
                <Typography variant={'body2'}>
                    {messageForPrivacyLevel(d.privacyLevel)}
                </Typography>
            </RichListItem>

            <RichListItem title={'Transactions'} help={'The total number of transactions involving this address.'}>
                <Typography variant={'body2'}>{d.txn}</Typography>
            </RichListItem>

            <RichListItem title={'First Block'}
                          help={'The height of the earliest block where this address discovered.'}>
                <SmartLink to={`/block/${d.firstBlockHeight}`}>
                    <Typography variant={'body2'}>{d.firstBlockHeight}</Typography>
                </SmartLink>
            </RichListItem>

            <RichListItem title={'Last Mined Block'}
                          help={'The height of the latest block where this address discovered.'}>
                <SmartLink to={`/block/${d.lastBlockHeight}`}>
                    <Typography variant={'body2'}>{d.lastBlockHeight}</Typography>
                </SmartLink>
            </RichListItem>
            <RichListItem title={'Total Received'} help={'The total amount received may be by this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel,d.totalReceived)}</Typography>
            </RichListItem>
            <RichListItem title={'Total Sent'} help={'The total amount sent may be by this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel,d.totalSent)}</Typography>
            </RichListItem>
            <RichListItem title={'Balance'} help={'The balance of this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel,d.balance)}</Typography>
            </RichListItem>
        </RichList>
    );
}

interface ViewAddressTxsProps {
    address: string;
    totalTxs: number;
    pageSize: number;
    pageNo: number;
}

function ViewAddressTxs({address, totalTxs, pageSize, pageNo}: ViewAddressTxsProps) {
    const addressTxSeqNoBegin = (pageNo - 1) * pageSize;
    const addressTxSeqNoEnd = addressTxSeqNoBegin + pageSize;

    return (
        <PaginationCard title={`Address:${address}${address && '*'.repeat(4)}`} totalItems={totalTxs} pageSize={pageSize} pageNo={pageNo}>
            <ViewAddressTxsContent
                address={address}
                totalTxs={totalTxs}
                addressTxSeqNoBegin={addressTxSeqNoBegin}
                addressTxSeqNotEnd={addressTxSeqNoEnd}
            />
        </PaginationCard>
    );
}


interface ViewAddressTxsContentProps {
    address:string,
    totalTxs:number,
    addressTxSeqNoBegin: number;
    addressTxSeqNotEnd: number;
}

function ViewAddressTxsContent({address,totalTxs,addressTxSeqNoBegin, addressTxSeqNotEnd}: ViewAddressTxsContentProps) {
    const fetchCount = 100;
    const startForAddressTxSeqnoBegin = Math.floor(addressTxSeqNoBegin / fetchCount) * fetchCount;
    const startForAddressTxSeqnoEnd = Math.floor(addressTxSeqNotEnd / fetchCount) * fetchCount;

    const qs = [
        useAddressTxs(address,startForAddressTxSeqnoBegin,fetchCount, {
            enabled: startForAddressTxSeqnoBegin !== startForAddressTxSeqnoEnd,
        }),
        useAddressTxs(address,startForAddressTxSeqnoEnd, fetchCount),
    ];

    if (qs.some((q) => q.isLoading) || qs.some((q) => q.isRefetching)) {
        return <LinearProgress color={'warning'}/>;
    }

    if (qs.some((q) => q.isError) || qs.some((q) => !q.isSuccess)) {
        return (
            <ErrorCard enableGoBack={true}>
                <Typography variant={'h4'}>Failed fetching transaction data for address.</Typography>
            </ErrorCard>
        );
    }

    let data =
        startForAddressTxSeqnoBegin === startForAddressTxSeqnoEnd ? qs[0].data.payload : [...qs[0].data.payload, ...qs[1].data.payload];
    const dataBegin = addressTxSeqNoBegin - startForAddressTxSeqnoBegin;
    const dataEnd = dataBegin + (addressTxSeqNotEnd - addressTxSeqNoBegin);
    data =data.filter((d: any, idx: number) => dataBegin <= idx && idx < dataEnd);

    return <TxList data={data}/>;
}

const allowedPageSizes = [10, 25, 50, 100];
const ViewAddress = () => {
    let {address} = useParams();
    if (address === undefined) {
        throw new Error('Illegal address.');
    }
    if (address === ''){
        throw new Error('Illegal address.');
    }
    if (address && !address.startsWith('0x')) {
        address = '0x' + address;
    }

    const [searchParams] = useSearchParams();
    let pageSize = Number(searchParams.get('ps') ?? 15);
    if (isNaN(pageSize) || !allowedPageSizes.includes(pageSize)) {
        pageSize = 15;
    }

    let pageNo = Number(searchParams.get('p') ?? 1);
    if (isNaN(pageNo) || pageNo < 1) {
        pageNo = 1;
    }
    let selectedTab=Number(searchParams.get('tab') ?? 0);

    return (
        <QueryProcessor
            query={useAddressWithPrecheck(address)}
            dataValidator={(data: any) => data.type === 'address'}
            renderer={(data: any) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography component={'span'} variant={'h2'}>
                            Address Details
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TabbedMainCard labels={['Overview', 'Transactions']} initialTab={selectedTab}>
                            <CardContent>
                                <AddressOverview d={data.payload}/>
                            </CardContent>
                            <CardContent>
                                <ViewAddressTxs
                                    address={address as string}
                                    totalTxs={data.payload.txn}
                                    pageNo={pageNo}
                                    pageSize={pageSize}
                                />
                            </CardContent>
                        </TabbedMainCard>
                    </Grid>
                </Grid>
            )}
        />
    );
};

export default ViewAddress;
